/**
 * media_keys.history[] — non-destructive media versioning, zero migration.
 *
 * `social_posts.media_keys` is already a jsonb column (it carries `format` +
 * `perPlatform` framing today). We piggyback a `history` array on it: each time
 * a slot's media is REPLACED (an AI edit or a picked variation), the prior
 * media is pushed here as a {url,prompt,model,seed,createdAt} entry so the
 * inspector can offer Original⇄Revised compare + Revert. Capped at 10 — beyond
 * that, the underlying storage objects are GC'd per the retention note (Task 40).
 *
 * Heavier media.parent_media_id lineage is explicitly deferred (spec §7).
 */

export interface MediaHistoryEntry {
  /** The prior media URL being archived. */
  url: string;
  /** The edit prompt that REPLACED this entry (provenance). */
  prompt?: string;
  /** Model that produced the replacement (e.g. nano banana pro). */
  model?: string;
  seed?: string | number;
  /** ISO timestamp the entry was archived. */
  createdAt: string;
}

export const MEDIA_HISTORY_CAP = 10;

function isEntry(v: unknown): v is MediaHistoryEntry {
  return (
    !!v &&
    typeof v === 'object' &&
    typeof (v as { url?: unknown }).url === 'string' &&
    typeof (v as { createdAt?: unknown }).createdAt === 'string'
  );
}

export function readMediaHistory(mediaKeys: unknown): MediaHistoryEntry[] {
  if (!mediaKeys || typeof mediaKeys !== 'object') return [];
  const raw = (mediaKeys as { history?: unknown }).history;
  if (!Array.isArray(raw)) return [];
  return raw.filter(isEntry);
}

export function pushMediaHistory(
  mediaKeys: unknown,
  entry: MediaHistoryEntry,
): Record<string, unknown> {
  const base =
    mediaKeys && typeof mediaKeys === 'object' ? { ...(mediaKeys as Record<string, unknown>) } : {};
  const history = [...readMediaHistory(mediaKeys), entry].slice(-MEDIA_HISTORY_CAP);
  return { ...base, history };
}
